import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

import AuthLayout from '../../shared/components/AuthLayout';
import Button from '../../shared/components/Button';
import Input from '../../shared/components/Input';
import OverlayLoader from '../../components/Loader/OverlayLoader';
import loginImage from '../../assets/images/login.png';
import { useAuth } from '../../shared/hooks/useAuth';
import { useAuthForm } from '../../shared/hooks/useAuthForm';
import { DASHBOARD_STACK } from '../../utils/screenNames';
import {
  loginWithEmailPhone,
  resetAccountPassword,
} from '../../redux/actions/authActions';
import { updateUserInfo } from '../../redux/slices/userSlice';

/**
 * ResetPasswordWithMobile Page - Refactored with Tailwind CSS
 * Enter reset code and new password for mobile number
 */
function ResetPasswordWithMobile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { telephone } = location?.state || {};

  const {
    values,
    errors,
    handleChange,
    handleBlur,
    validatePassword,
    validate,
    setError,
  } = useAuthForm({ resetCode: '', password: '' });

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  /**
   * Validate reset code
   */
  const validateResetCode = (code) => {
    if (!code) {
      return 'Please enter the reset code';
    }
    if (code.length !== 6) {
      return 'Reset code must be 6 digits';
    }
    return '';
  };

  /**
   * Handle password reset
   */
  const handleResetPassword = async () => {
    // Validate form
    const isValid = validate({
      resetCode: validateResetCode,
      password: validatePassword,
    });

    if (!isValid) return;

    setIsLoading(true);

    try {
      await resetAccountPassword(
        null,
        telephone,
        values.password,
        values.resetCode,
        async () => {
          // Auto-login after successful reset
          await loginWithEmailPhone(
            null,
            telephone,
            values.password,
            (userData) => {
              const { access, refresh } = userData.tokens;
              window.localStorage.setItem(
                'username',
                userData?.user?.profile?.fullName
              );
              window.localStorage.setItem('access', JSON.stringify(access));
              window.localStorage.setItem('refresh', JSON.stringify(refresh));

              dispatch(updateUserInfo({ userData: { ...userData.user } }));
              navigate(DASHBOARD_STACK.HOME);
            },
            () => {
              setIsLoading(false);
              setError('password', 'Login failed after reset');
            }
          );
        },
        () => {
          setIsLoading(false);
          setError('resetCode', 'Failed to reset password');
        }
      );
    } catch (error) {
      setIsLoading(false);
      setError('password', 'An error occurred. Please try again.');
    }
  };

  if (isLoading) {
    return <OverlayLoader />;
  }

  return (
    <div className="relative w-screen h-screen bg-bg-dark">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-6 left-6 z-20 flex items-center gap-2 text-shaded-text hover:text-[#FEFCEB] transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-proxima text-sm">Back</span>
      </button>

      {/* Main Content */}
      <AuthLayout
        heroImage={loginImage}
        title="Reset your password"
        subtitle="Enter the code we sent to your mobile number."
      >
        {/* Phone Number (Disabled) */}
        <div className="w-full">
          <label className="block text-[#FEFCEB] text-sm font-proxima mb-2">
            Phone Number
          </label>
          <Input
            type="text"
            value={telephone || ''}
            disabled
            className="opacity-60"
          />
        </div>

        {/* Reset Code Input */}
        <div className="w-full mt-4">
          <label className="block text-[#FEFCEB] text-sm font-proxima mb-2">
            Reset Code
          </label>
          <Input
            type="text"
            name="resetCode"
            placeholder="6-digit code"
            value={values.resetCode}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.resetCode}
            errorMessage={errors.resetCode}
            maxLength={6}
          />
        </div>

        {/* New Password Input */}
        <div className="w-full mt-4">
          <label className="block text-[#FEFCEB] text-sm font-proxima mb-2">
            New Password
          </label>
          <Input
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="New password"
            value={values.password}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.password}
            errorMessage={errors.password}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-shaded-text hover:text-[#FEFCEB] transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            }
          />
        </div>

        {/* Reset Password Button */}
        <Button
          variant="primary"
          size="lg"
          onClick={handleResetPassword}
          className="w-full mt-6"
          disabled={!values.resetCode || !values.password}
        >
          Reset password
        </Button>
      </AuthLayout>
    </div>
  );
}

export default ResetPasswordWithMobile;
